import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Search, Truck, X } from 'lucide-react';
import { posApi } from '@/services/api';
import { SupplierOption } from './types';

interface Props {
  value: string;
  label: string;
  onChange: (supplierId: string, label: string) => void;
}

export default function SupplierFilter({ value, label, onChange }: Props) {
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const { data: suppliersRes } = useQuery({
    queryKey: ['suppliersForPurchaseReport', search],
    queryFn: async () => {
      const res = await posApi.getSuppliers({ search, limit: 50 });
      return res.data.message;
    },
  });

  const suppliers: SupplierOption[] = suppliersRes?.suppliers || suppliersRes || [];

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const select = (supplier?: SupplierOption) => {
    if (!supplier) {
      onChange('', '');
    } else {
      onChange(
        supplier.name,
        supplier.mobile ? `${supplier.supplier_name} - ${supplier.mobile}` : supplier.supplier_name,
      );
    }
    setSearch('');
    setOpen(false);
  };

  return (
    <div className="space-y-1.5 flex-1 min-w-[240px] relative" ref={ref}>
      <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
        <Truck className="w-4 h-4 text-gray-400" />
        Supplier
      </label>
      {value ? (
        <div className="input-field flex items-center justify-between gap-2 py-2">
          <span className="truncate text-sm text-gray-900">{label}</span>
          <button
            type="button"
            onClick={() => select()}
            className="p-1 text-gray-400 hover:text-gray-600 rounded"
            aria-label="Clear supplier"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      ) : (
        <>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              className="input-field pl-9"
              placeholder="Search by name or phone..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setOpen(true);
              }}
              onFocus={() => setOpen(true)}
            />
          </div>
          {open && (
            <div className="absolute z-20 left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-56 overflow-auto">
              <button
                type="button"
                onClick={() => select()}
                className="w-full text-left px-3 py-2.5 text-sm hover:bg-gray-50 border-b border-gray-100 text-gray-700"
              >
                All Suppliers
              </button>
              {suppliers.length > 0 ? (
                suppliers.map((s) => (
                  <button
                    key={s.name}
                    type="button"
                    onClick={() => select(s)}
                    className="w-full text-left px-3 py-2.5 text-sm hover:bg-gray-50"
                  >
                    <p className="font-medium text-gray-900">{s.supplier_name}</p>
                    {s.mobile && <p className="text-xs text-gray-500">{s.mobile}</p>}
                  </button>
                ))
              ) : (
                <div className="px-3 py-4 text-sm text-gray-400 text-center">No suppliers found</div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
